import React from 'react';
import { Row, Col, Space, Tag, Typography, DatePicker, Empty } from 'antd';
import { DownOutlined, CloseOutlined } from '@ant-design/icons';
import { primaryShades } from '../../../theme/theme';
import { PrimaryButton, OutlineButton } from '../Buttons/Buttons';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Costanti dei colori
const PRIMARY_700 = primaryShades.primary700;
const MEDIUM_GREY = '#999';

// Componente Tag personalizzato e riutilizzabile
const TagItem = ({ children, ...props }) => {
    return (
        <Tag
            {...props}
            style={{
                marginBottom: 'clamp(0.25rem, 0.5vw, 0.375rem)',
                marginRight: 'clamp(0.25rem, 0.5vw, 0.375rem)',
                backgroundColor: '#f5f5f5',
                border: '1px solid #e8e8e8',
                borderRadius: '2px',
                ...props.style
            }}
        >
            {children}
        </Tag>
    );
};

const FilterComponent = ({
    // Stato e controllo del filtro
    isFilterOpen,
    onToggleFilter,

    // Props per il date picker
    dateRange,
    onChangeDateRange,
    presetLabel,
    rangePresets,

    // Custom content che può sostituire il date picker
    customLeftContent,

    // Props per i filtri
    onClearFilters,
    onApplyFilter,

    // Props per download report
    downloadReportButton,

    // Contenuto filtri standard
    catalogSection,
    catalogSectionText = "Select Catalogue",
    productsSection,
    productsSectionText = "Select Products",
    serviceSection,
    serviceSectionText = "Select Service",

    // Filtri personalizzati aggiuntivi
    customFilterSections = [],

    // Visualizzazione tag dei filtri selezionati
    renderSelectedFilters,
    hasSelectedFilters,
    isFiltersApplied
}) => {
    // Dropdown content
    const filterContent = (
        <div style={{
            marginTop: 'clamp(1.5rem, 4vw, 2.25rem)',
            animation: 'filterAnimation 0.3s ease-in-out',
        }}>
            <style>
                {`
                @keyframes filterAnimation {
                    0% { opacity: 0; transform: translateY(clamp(-0.625rem, -2vw, -0.9375rem)); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                `}
            </style>
            <Row gutter={[16, 24]}>
                <Col span={24}>
                    <Row gutter={[24, 0]}>
                        {/* Colonna sinistra per i filtri */}
                        <Col span={8}>
                            {/* Sezione catalogo (sempre presente) */}
                            {catalogSection && (
                                <div style={{ marginBottom: 'clamp(0.75rem, 3vw, 1.5rem)' }}>
                                    <Text>{catalogSectionText}</Text>
                                    <div style={{ position: "relative", width: "100%" }}>
                                        {catalogSection}
                                    </div>
                                </div>
                            )}

                            {/* Sezione prodotti (standard) */}
                            {productsSection && (
                                <div style={{ marginBottom: 'clamp(0.75rem, 3vw, 1.5rem)' }}>
                                    <Text>{productsSectionText}</Text>
                                    {productsSection}
                                </div>
                            )}

                            {/* Sezione servizi (standard) */}
                            {serviceSection && (
                                <div style={{ marginBottom: 'clamp(0.75rem, 3vw, 1.5rem)' }}>
                                    <Text>{serviceSectionText}</Text>
                                    <div style={{ position: "relative", width: "100%" }}>
                                        {serviceSection}
                                    </div>
                                </div>
                            )}

                            {/* Sezioni di filtri personalizzate aggiuntive */}
                            {customFilterSections.map((section, index) => (
                                <div key={`custom-section-${index}`} style={{ marginBottom: 'clamp(0.75rem, 3vw, 1.5rem)' }}>
                                    {section.title && <Text>{section.title}</Text>}
                                    <div style={{ position: "relative", width: "100%" }}>
                                        {section.content}
                                    </div>
                                </div>
                            ))}
                        </Col>

                        {/* Colonna destra per i filtri selezionati */}
                        <Col span={16}>
                            <div style={{
                                height: '100%',
                                padding: '0 clamp(0.3125rem, 2vw, 0.625rem)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: hasSelectedFilters ? 'flex-start' : 'center'
                            }}>
                                {/* Mostri i tag nella colonna destra quando il filtro è aperto */}
                                {hasSelectedFilters ? (
                                    <div style={{ textAlign: 'left', marginBottom: 'clamp(0.5rem, 2vw, 1rem)' }}>
                                        <Text style={{ display: 'block', marginBottom: 'clamp(0.25rem, 1vw, 0.50rem)' }}>Selected Filters:</Text>
                                        {renderSelectedFilters(true)}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', color: MEDIUM_GREY }}>
                                        <Text style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', color: MEDIUM_GREY }}>No filters selected</Text>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* Footer buttons */}
            <Row justify="end">
                <Space size={16} style={{ display: 'flex', alignItems: 'center' }}>
                    <OutlineButton
                        onClick={onClearFilters}
                    >
                        Clear Filters
                    </OutlineButton>
                    <PrimaryButton
                        onClick={onApplyFilter}
                    >
                        Apply Filters
                    </PrimaryButton>
                </Space>
            </Row>
        </div>
    );

    // Render del componente principale
    return (
        <div>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1  // Questo permette al contenitore sinistro di espandersi
                }}>
                    {customLeftContent ? (
                        // Usa il contenuto personalizzato se fornito
                        customLeftContent
                    ) : (
                        // Altrimenti usa il date picker standard
                        <>
                            <RangePicker
                                value={dateRange}
                                presets={rangePresets}
                                onChange={onChangeDateRange}
                                format="DD/MM/YYYY"
                                placement={"bottomLeft"}
                            />
                            <Text
                                style={{
                                    marginLeft: 'clamp(0.5rem, 2vw, 1rem)',
                                    fontWeight: 'medium',
                                    color: PRIMARY_700,
                                    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)'
                                }}
                            >
                                {presetLabel ?
                                    presetLabel :
                                    (dateRange && dateRange[0] && dateRange[1] ?
                                        `${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}` :
                                        'Select Date Range')}
                            </Text>
                        </>
                    )}
                    {downloadReportButton && (
                        <div style={{
                            marginLeft: 'clamp(1rem, 3vw, 1.5rem)',
                        }}>
                            {downloadReportButton}
                        </div>
                    )}
                </div>

                <PrimaryButton
                    onClick={onToggleFilter}
                >
                    {isFilterOpen ? 'Close Filters' : 'Open Filters'}
                    <div style={{
                        marginLeft: 'clamp(0.25rem, 1.5vw, 0.5rem)',
                        transition: 'transform 0.3s ease-in-out',
                        transform: isFilterOpen ? 'rotate(180deg)' : 'rotate(0)',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        {isFilterOpen ? <CloseOutlined /> : <DownOutlined />}
                    </div>
                </PrimaryButton>
            </div>

            {/* Dropdown del filtro con transizione */}
            <div style={{
                overflow: 'hidden',
                maxHeight: isFilterOpen ? 'clamp(31.25rem, 90vh, 62.5rem)' : '0',
                opacity: isFilterOpen ? 1 : 0,
                transition: 'all 0.3s ease-in-out'
            }}>
                {filterContent}
            </div>

            {/* Tags delle selezioni */}
            {!isFilterOpen && isFiltersApplied && renderSelectedFilters(false)}
        </div>
    );
};

// Esporta anche il componente TagItem per riutilizzo
export { TagItem };
export default FilterComponent; 